import { Select } from '@mantine/core';
import { Block, transitionsDelay } from '../../styles/basicStyles';
import { breakpoints } from '../../styles/breakpoints';
import styled, { css } from 'styled-components';

export const InputWrapper = styled.div<{ hasError?: boolean; variant?: 'primary' | 'secondary'; search?: boolean }>`
  width: 100%;

  & label,
  & .mantine-CheckboxGroup-label,
  & .mantine-RadioGroup-label {
    font-family: 'Graphie', sans-serif;
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1.688rem;
    color: ${({ theme }) => theme.colors.black};
    padding-bottom: 0.5rem;

    @media ${breakpoints.sm} {
      font-size: 1.125rem;
    }
  }

  & input::placeholder,
  & textarea::placeholder {
    font-family: 'Graphie', sans-serif;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.placeholderColor};
  }

  & input::placeholder,
  & textarea::placeholder,
  & input,
  & textarea {
    font-size: 1.125rem;
    line-height: 1.688rem;

    @media ${breakpoints.sm} {
      font-size: 1rem;
    }
  }

  /* TEXT */

  & .mantine-TextInput-wrapper input,
  & .mantine-Textarea-wrapper textarea {
    background-color: ${({ theme }) => theme.colors.white} !important;
    border: 0.063rem solid ${({ theme, hasError }) => hasError ? theme.colors.errorColor : theme.colors.borderColor};
    border-radius: ${({ search }) => search ? '3.125rem' : '0.5rem'};
    min-height: unset;
    height: auto;
    font-family: 'Graphie', sans-serif;
    font-weight: 600;
    color: ${({ theme, hasError }) => hasError ? theme.colors.errorColor : theme.colors.black } !important;
    transition: border ${transitionsDelay} linear;
    padding: ${({ search }) => search ? '0.532rem 3.125rem' : '1.125rem 2rem'};

    &:disabled {
      border: 0.063rem solid ${({ theme }) => theme.colors.disabledColor};
      color: ${({ theme }) => theme.colors.disabledColor} !important;
    }

    &:focus {
      border: 0.063rem solid ${({ theme }) => theme.colors.purple};
    }
  }

  /* CHECKBOX */

  & .mantine-Checkbox-root {
    & .mantine-Checkbox-body {
      display: flex;
      align-items: center;
      flex-direction: row;

      & .mantine-Checkbox-label {
        font-size: 0.938rem;
        font-weight: 350;
        line-height: 1.688rem;
        color: ${({ theme }) => theme.colors.chineseBlack};
        cursor: pointer;

        @media ${breakpoints.sm} {
          font-size: 0.875rem;
          line-height: 1.375rem;
        }
      }

      & .mantine-Checkbox-inner {    
        & input {
          transition: background-color ${transitionsDelay} linear, border ${transitionsDelay} linear;
          background-color: ${({ theme }) => theme.colors.white};
          border: 0.063rem solid ${({ theme, hasError }) => hasError ? theme.colors.errorColor : theme.colors.borderColor};
          cursor: pointer;

          &:checked {
            background-color: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.chineseBlack : theme.colors.purple};
            border: 0.063rem solid ${({ theme, variant }) => variant === 'secondary' ? theme.colors.chineseBlack : theme.colors.purple};
          }

          &:checked + :disabled {
            background-color: ${({ theme }) => theme.colors.disabledColor};
            border: 0.063rem solid ${({ theme }) => theme.colors.disabledColor};
          }

          &:disabled {
            opacity: 0.5;
          }

          &:disabled + .___ref-icon {
            color: ${({ theme }) => theme.colors.white} !important;
          }
        }
      }

      & .mantine-Checkbox-label {
        padding-bottom: 0;
      }

      & .mantine-Checkbox-inner,
      & .mantine-Checkbox-inner input {
        width: 1.875rem;
        height: 1.875rem;
      }

      & svg {
        width: 1rem;
        height: 1rem;
      }
    }
  }

  /* CHECKBOX/RADIO GROUP */
  
  & .mantine-CheckboxGroup-root .mantine-Checkbox-body .mantine-Checkbox-label,
  & .mantine-RadioGroup-root .mantine-Radio-body .mantine-Radio-label {
    font-size: 1.125rem;
    line-height: 1.688rem;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.black};

    @media ${breakpoints.lg} {
      font-size: 1rem;
    }
  } 

  /* RADIO GROUP */

  & .mantine-RadioGroup-root {
    & .mantine-Radio-inner {
      display: none;
    }

    & .mantine-Radio-labelWrapper {
      & .mantine-Radio-label {
        padding: 0;
        cursor: pointer;
        padding: 1.25rem 0.5rem;
        transition: color ${transitionsDelay} linear;
      }
    }
  }

  & .mantine-TextInput-required,
  & .mantine-Textarea-required,
  & .mantine-CheckboxGroup-required,
  & .mantine-RadioGroup-required {
    color: ${({ theme }) => theme.colors.purple};
  }

  // Disable autofill style
  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 600000s 0s, color 600000s 0s;
  }

  input[data-autocompleted] {
    background-color: transparent !important;
  }
`;

export const SelectWrapper = styled(Select)<{error?: boolean, bgColor?: string, selected?: boolean}>`
  width: 100%;
  position: relative;

  & .mantine-Select-label {
    position: absolute;
    top: 0.4rem;
    left: 1.5rem;
    font-size: 0.75rem;
    line-height: 1.125rem;
    font-weight: 400;
    color: ${({ theme, error }) => error ? theme.colors.errorColor : '#4B495F'};
    z-index: 999;
    font-family: 'Graphie', sans-serif;

    @media ${breakpoints.md} {
      display: none;
    }
  }

  & .mantine-Select-input {
    background-color:${({ bgColor }) => bgColor ? bgColor : 'transparent'};
    border: ${({ theme, error }) =>  error ? `0.094rem solid ${theme.colors.errorColor}` : `0.094rem solid ${theme.colors.mercury}`};
    border-radius: 6.188rem;
    min-height: unset;
    height: auto;
    font-family: 'Graphie', sans-serif;
    color: ${({ theme, selected }) => selected ? theme.colors.white : theme.colors.black};
    font-weight: 400;
    padding-bottom: 0.532rem;
    padding-top: 1.5rem;
    padding-left: 1.5rem;
    font-size: 1.125rem;
    line-height: 1.688rem;

    &::placeholder {
      font-family: 'Graphie', sans-serif;
      font-weight: 400;
      color: ${({ theme }) => theme.colors.chineseBlack};
    }

    @media ${breakpoints.lg} {
      font-size: 1rem;
    }

    @media ${breakpoints.md} {
      padding-top: 0.532rem;
      border: ${({ theme, error }) =>  error ? `0.094rem solid ${theme.colors.errorColor}` : `0.094rem solid ${theme.colors.black}`};
    }
  }

  & .mantine-Select-dropdown {
    background: ${({ theme }) => theme.colors.white};
    border: 0.063rem solid ${({ theme }) => theme.colors.borderColor};
    border-radius: 0.25rem;

    & .mantine-Select-itemsWrapper {
      padding: 0;

      & .mantine-Select-item {
        font-family: 'Graphie', sans-serif !important;
        color: ${({ theme }) => theme.colors.labelColor};
        transition: background-color ${transitionsDelay} linear;
        border-radius: 0;

        &:first-child {
          border-top-right-radius: 0.25rem;
          border-top-left-radius: 0.25rem;
        }

        &:last-child {
          border-bottom-right-radius: 0.25rem;
          border-bottom-left-radius: 0.25rem;
        }

        &[data-hovered] {
          background-color: ${({ theme }) => theme.colors.g500};
        }

        &[data-selected] {
          background-color: ${({ theme }) => theme.colors.g400};
        }
      }
    }
  } 
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.errorColor};
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.063rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-left: 0.75rem;
`;

export const RadioWrapper = styled.div`
  width: 25%;
  display: flex;

  @media ${breakpoints.sm} {
    width: 50%;

    &:nth-child(odd) {
      padding-right: 0.063rem;
      padding-bottom: 0.125rem;
    }

    &:nth-child(even) {
      padding-left: 0.063rem;
      padding-bottom: 0.125rem;
    }
  }

  @media ${breakpoints.xs} {
    width: 100%;

    &:nth-child(odd) {
      padding-right: 0;
    }

    &:nth-child(even) {
      padding-left: 0rem;
    }
  }
`;

export const RadioContent = styled.div<{ isSelected: boolean }>`
  background-color: ${({ theme, isSelected }) => isSelected ? theme.colors.lavender : theme.colors.cultured};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color ${transitionsDelay} linear;
  width: 100%;

  ${({ isSelected }) => isSelected &&
    css`
      & .mantine-Radio-label {
        color: #493C94 !important;
      }
    `
  }

  & .mantine-Radio-root {
    width: 100%;

    & .mantine-Radio-body {
      display: block;
      text-align: center;
      width: 100%;

      & .mantine-Radio-labelWrapper {
        width: 100%;
      }
    }
  }
`;

export const RadioSeparator = styled.div<{ isSelected: boolean }>`
  height: 1.688rem;
  width: 0.063rem;
  background-color: ${({ theme, isSelected }) => isSelected ? theme.colors.lavender : theme.colors.lightSilver};
  transition: background-color ${transitionsDelay} linear;

  @media ${breakpoints.sm} {
    display: none;
  }
`;

export const RadioButtons = styled(Block)<{ hasError: boolean }>`
  flex-flow: row wrap;
  display: flex;
  width: 100%;
  border: 0.063rem solid ${({ hasError, theme }) => hasError ? theme.colors.errorColor : 'transparent'};
`;
